import React from 'react';
import { connect } from 'react-redux';
import { Button, Field, TextInput, SelectInput, ConfirmModal, StatusModal } from '../../components/ui';
import FlatList from './FlatList';
import EntityEditor, { EmptyEditor } from './EntityEditor';
import { postJson, del, withOrig, isDraftDirty } from './shared';
import { setAdminState, setAdminControl, getCompanies, getLpns, getPeople } from '../../slices';

/*
 * CataloguePane — shared master-detail for the paginated catalogue entities:
 * Companies, License Plates, Drivers (People). One component, three configs
 * (see COMPANY / LPN / PERSON exports below). CRUD copied verbatim from
 * CompanyControl/Form, LpnControl/Form, PersonControl/Form.
 *
 * These entities are SERVER-PAGINATED (10/page). The list is driven entirely
 * server-side: the search box, the customer filter, and (for Plates/Drivers)
 * the company filter are sent to the fetch thunk as {name, customerId,
 * companyId}, so a match on any page is found — not just the current page. The
 * old Prev/Next pager is replaced with infinite-scroll: page 1 loads on
 * mount/filter-change (replacing the list), and scrolling to the bottom appends
 * the next page (the slice reducers append when pageIndex > 1).
 *
 * Config shape:
 *   key, title, addLabel, entityLabel, searchPlaceholder, hasCompanyFilter
 *   columns: { primary(row), secondary(row) }
 *   saveUrl, deleteUrl(id), ... (CRUD, verbatim)
 */
const SEARCH_DEBOUNCE = 300; // ms; server paginates 10/page

export class CataloguePane extends React.Component {
  // `items`/`total` are accumulated HERE (not in Redux) so infinite scroll never
  // bloats the persisted store, and a request-sequence guard (`_seq`) discards
  // stale/out-of-order responses when filters change mid-flight.
  state = {
    draft: null, isNew: false, saving: false, deleting: false, confirm: null, saveResult: null,
    page: 1, search: '', customerId: '', companyId: '',
    items: [], total: 0, loading: false, loadingMore: false, hasMore: false,
  };
  _seq = 0;

  componentDidMount() {
    this.loadFirstPage();
  }

  componentWillUnmount() {
    clearTimeout(this._searchT);
    this._seq++; // invalidate any in-flight fetch so it can't setState post-unmount
  }

  // Assemble the server filter body from the active search + filter selects.
  buildFilters = (page) => {
    const f = { page };
    const s = this.state.search.trim();
    if (s) f.name = s;
    if (this.state.customerId) f.customerId = this.state.customerId;
    if (this.props.cfg.hasCompanyFilter && this.state.companyId) f.companyId = this.state.companyId;
    return f;
  };

  // Reset to page 1 with the current filters. Bumps the sequence so any older
  // in-flight fetch (first-page or load-more) is ignored when it resolves.
  loadFirstPage = async () => {
    const seq = ++this._seq;
    this.setState({ loading: true, page: 1 });
    const action = await this.props.fetchPage(this.buildFilters(1));
    if (seq !== this._seq) return; // superseded by a newer reset — discard
    const payload = action && action.payload;
    const total = payload ? payload.count : undefined;
    if (total === undefined) { this.setState({ loading: false }); return; } // fetch failed — keep prior list
    const rows = payload.rows || [];
    this.setState({ items: rows, total, loading: false, hasMore: rows.length < total });
  };

  // Infinite scroll — append the next page. Tagged with the current sequence so
  // a page that resolves after a filter change is dropped (never merged).
  loadMore = async () => {
    if (this.state.loading || this.state.loadingMore || !this.state.hasMore) return;
    const seq = this._seq;
    const next = this.state.page + 1;
    this.setState({ loadingMore: true });
    const action = await this.props.fetchPage(this.buildFilters(next));
    if (seq !== this._seq) { this.setState({ loadingMore: false }); return; } // filters changed — drop
    const payload = action && action.payload;
    const total = payload ? payload.count : undefined;
    if (total === undefined) { this.setState({ loadingMore: false }); return; } // failed — allow retry
    const rows = payload.rows || [];
    this.setState((st) => {
      // Dedupe by id so a row that appears on two pages can't produce a duplicate
      // React key (belt-and-suspenders with the backend's id tie-breaker).
      const seen = new Set(st.items.map((r) => r.id));
      const items = st.items.concat(rows.filter((r) => !seen.has(r.id)));
      // rows.length === 0 means we've paged past the end.
      return { items, total, page: next, loadingMore: false, hasMore: rows.length > 0 && items.length < total };
    });
  };

  // Debounced live search (server-side).
  onSearchChange = (value) => {
    this.setState({ search: value });
    clearTimeout(this._searchT);
    this._searchT = setTimeout(this.loadFirstPage, SEARCH_DEBOUNCE);
  };

  // Filter selects reload immediately (cancel any pending search debounce first).
  // Changing customer clears the company filter (its options narrow to the customer).
  onCustomerFilter = (value) => { clearTimeout(this._searchT); this.setState({ customerId: value, companyId: '' }, this.loadFirstPage); };
  onCompanyFilter = (value) => { clearTimeout(this._searchT); this.setState({ companyId: value }, this.loadFirstPage); };

  refresh = () => {
    // bump adminListReload so any listeners refetch; reload page 1 (scroll resets
    // to top) so a create/edit/delete is reflected honestly against the server.
    this.props.setAdminState({ field: 'adminListReload', value: this.props.adminListReload + 1 });
    this.loadFirstPage();
  };

  newItem = () => this.setState({ isNew: true, draft: withOrig(this.props.cfg.buildNewDraft(this.props)) });
  editItem = (row) => {
    const base = this.props.cfg.buildEditDraft(row);
    // Hydrate the filtered company list for entities that have one (Lpn/Person).
    // Mirrors the original forms, which set companies = props.companies on edit.
    if (base.companies === null) {
      base.companies = (this.props.companies || []).filter(
        (c) => c.GateCustomer && c.GateCustomer.id === parseInt(base.customerId, 10)
      );
    }
    this.setState({ isNew: false, draft: withOrig({ ...base, _row: row }) });
  };

  save = async () => {
    const cfg = this.props.cfg;
    const d = this.state.draft;
    if (cfg.preSaveGuard && !cfg.preSaveGuard(d)) {
      this.setState({ saveResult: { status: 'error', title: 'Save failed', message: cfg.preSaveGuardMessage } });
      return;
    }
    const wasNew = this.state.isNew;
    this.setState({ saving: true });
    try {
      const res = await postJson(cfg.saveUrl, cfg.buildBody(d), this.props.webToken);
      this.setState({ saving: false });
      if (res && res.error === 'duplicate') { this.setState({ saveResult: { status: 'error', title: 'Save failed', message: cfg.duplicateMessage(d) } }); return; }
      if (res && res.error) { this.setState({ saveResult: { status: 'error', title: 'Save failed', message: 'Please review the fields and try again.' } }); return; }
      if (wasNew) {
        // Reopen only a record identified by the save response. Older backends may return id:0;
        // names are not reliable identities, so in that case return to the refreshed list.
        const newId = res && res.id;
        this.props.setAdminState({ field: 'adminListReload', value: this.props.adminListReload + 1 });
        const seq = ++this._seq;
        const action = await this.props.fetchPage(newId ? { id: newId, page: 1 } : this.buildFilters(1));
        if (seq !== this._seq) return;
        const rows = (action && action.payload && action.payload.rows) || [];
        this.setState({
          isNew: false, draft: null, page: 1, items: rows,
          total: action && action.payload ? action.payload.count : rows.length,
          hasMore: !!(action && action.payload && rows.length < action.payload.count), loading: false,
          saveResult: { status: 'success', title: 'Saved', message: `${cfg.entityLabel} created.` },
        });
        const match = newId
          ? rows.find((r) => String(r.id) === String(newId))
          : null;
        if (match) this.editItem(match);
      } else {
        this.refresh();   // keep the edited record shown; just reload the list to reflect the change
        // re-baseline the draft to the saved values so the editor reads CLEAN (Save disables until the
        // next change) instead of staying "dirty" against the pre-save snapshot.
        this.setState((st) => ({ draft: st.draft ? withOrig({ ...st.draft }) : st.draft, saveResult: { status: 'success', title: 'Saved', message: `${cfg.entityLabel} saved.` } }));
      }
    } catch (e) {
      console.error('Error:', e);
      this.setState({ saving: false, saveResult: { status: 'error', title: 'Save failed', message: 'If the problem continues please contact your installer.' } });
    }
  };

  askDelete = () => this.setState({ confirm: { row: this.state.draft._row } });
  doDelete = async () => {
    const cfg = this.props.cfg;
    const row = this.state.confirm.row;
    this.setState({ deleting: true });
    try {
      await del(cfg.deleteUrl(row.id), this.props.webToken);
      this.setState({ deleting: false, confirm: null });
      this.refresh();
      this.clear();   // the entity is gone, so the editor correctly clears
      this.setState({ saveResult: { status: 'success', title: 'Deleted', message: `${cfg.entityLabel} deleted.` } });
    } catch (e) {
      console.log(`${cfg.entityLabel} delete failed`, e);
      this.setState({ deleting: false, confirm: null, saveResult: { status: 'error', title: 'Delete failed', message: 'If the problem continues please contact your installer.' } });
    }
  };

  clear = () => this.setState({ draft: null, isNew: false });
  setDraft = (patch) => this.setState((st) => ({ draft: { ...st.draft, ...patch } }));

  renderEditor() {
    const cfg = this.props.cfg;
    const readOnly = this.props.readOnly;
    const { draft, isNew, saving, deleting } = this.state;
    if (!draft) {
      const msg = readOnly ? `Pick a ${cfg.entityLower} from the list to view its details.` : `Pick a ${cfg.entityLower} from the list, or add a new one.`;
      return <EmptyEditor icon={cfg.icon} title={`Select a ${cfg.entityLower}`} message={msg} />;
    }
    return (
      <EntityEditor
        title={isNew ? `New ${cfg.entityLabel}` : cfg.editorTitle(draft)}
        crumbs={[cfg.title]}
        onSubmit={this.save}
        onCancel={this.clear}
        onDelete={(isNew || readOnly) ? undefined : this.askDelete}
        saving={saving}
        deleting={deleting}
        saveLabel={isNew ? `Create ${cfg.entityLower}` : 'Save changes'}
        canSave={cfg.validate(draft) && isDraftDirty(draft)}
        readOnly={readOnly}
      >
        {/* [86bbumedw] P2 — a disabled <fieldset> makes every field inside read-only in one shot. */}
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          {cfg.renderFields(this, draft, isNew)}
        </fieldset>
      </EntityEditor>
    );
  }

  // Customer + (Plates/Drivers) company filter selects, shown under the search.
  renderToolbar() {
    const cfg = this.props.cfg;
    const customers = this.props.customers || [];
    const showCustomer = customers.length > 1;
    let companyOpts = null;
    if (cfg.hasCompanyFilter) {
      const all = this.props.companies || [];
      companyOpts = this.state.customerId
        ? all.filter((c) => c.GateCustomer && String(c.GateCustomer.id) === String(this.state.customerId))
        : all;
    }
    if (!showCustomer && !cfg.hasCompanyFilter) return null;
    return (
      <>
        {showCustomer && (
          <SelectInput size="sm" value={this.state.customerId} onChange={(e) => this.onCustomerFilter(e.target.value)} aria-label="Filter by customer">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.sName}</option>)}
          </SelectInput>
        )}
        {cfg.hasCompanyFilter && (
          <SelectInput size="sm" value={this.state.companyId} onChange={(e) => this.onCompanyFilter(e.target.value)} aria-label="Filter by company">
            <option value="">All companies</option>
            {companyOpts.map((c) => <option key={c.id} value={c.id}>{c.sName}</option>)}
          </SelectInput>
        )}
      </>
    );
  }

  render() {
    const cfg = this.props.cfg;
    const rows = this.state.items;
    const total = this.state.total;
    const selectedKey = this.state.draft && !this.state.isNew ? this.state.draft._row.id : (this.state.isNew ? '__new__' : undefined);
    const canAdd = this.props.customers && this.props.customers.length > 0;
    const filterActive = !!(this.state.search.trim() || this.state.customerId || this.state.companyId);
    const emptyText = filterActive ? `No ${cfg.entityLower}s match your filters.` : `No ${cfg.entityLower}s yet.`;
    return (
      <div className="setup">
        <FlatList
          title={cfg.title}
          items={rows}
          getKey={(r) => r.id}
          getPrimary={cfg.columns.primary}
          getSecondary={cfg.columns.secondary}
          selectedKey={selectedKey}
          onSelect={this.editItem}
          onAdd={(!this.props.readOnly && canAdd) ? this.newItem : undefined}
          onBack={() => this.props.setAdminControl(false)}
          addLabel={cfg.addLabel}
          loading={this.state.loading}
          searchValue={this.state.search}
          onSearchChange={this.onSearchChange}
          searchPlaceholder={cfg.searchPlaceholder}
          toolbar={this.renderToolbar()}
          onScrollEnd={this.loadMore}
          loadingMore={this.state.loadingMore}
          emptyText={emptyText}
          footer={
            <>
              <span className="sub num">{total.toLocaleString()} {total === 1 ? cfg.entityLower : cfg.entityPlural}</span>
              <div className="grow" />
              <Button size="sm" variant="ghost" onClick={() => this.props.setAdminControl(false)}>Done</Button>
            </>
          }
        />
        {this.renderEditor()}
        {this.state.confirm && (
          <ConfirmModal
            open
            onClose={() => this.setState({ confirm: null })}
            onConfirm={this.doDelete}
            onCancel={() => this.setState({ confirm: null })}
            title={`Delete this ${cfg.entityLower}?`}
            message={`This action cannot be undone. ${cfg.deleteName(this.state.confirm.row)}`}
            confirmLabel="Delete"
            danger
            loading={this.state.deleting}
          />
        )}
        {this.state.saveResult && (
          <StatusModal
            open
            status={this.state.saveResult.status}
            title={this.state.saveResult.title}
            message={this.state.saveResult.message}
            onClose={() => this.setState({ saveResult: null })}
          />
        )}
      </div>
    );
  }
}

/* ---------- shared field helpers (Customer + Company selects, verbatim behavior) ---------- */
function customerCompanyFields(self, draft, isNew, opts) {
  // opts.extra(self, draft) renders the entity-specific fields (name / first+last)
  const customers = self.props.customers;
  return (
    <div className="formgrid">
      <Field label="Customer" required>
        <SelectInput value={draft.customerId} disabled={!isNew} onChange={(e) => onCustomerChange(self, e.target.value)} placeholder="Please select">
          {customers.map((c) => <option key={c.id} value={c.id}>{c.sName}</option>)}
        </SelectInput>
      </Field>
      <Field label="Company" required error={draft.companies && draft.companies.length === 0 ? 'No available companies for the selected customer.' : undefined}>
        {draft.companies && draft.companies.length > 0 ? (
          <SelectInput value={draft.companyId} disabled={!isNew} onChange={(e) => self.setDraft({ companyId: e.target.value })} placeholder="Please select">
            {draft.companies.map((co) => <option key={co.id} value={co.id}>{co.sName}</option>)}
          </SelectInput>
        ) : (
          <TextInput value="" disabled placeholder="No available companies" />
        )}
      </Field>
      {opts.extra(self, draft)}
    </div>
  );
}
// verbatim from Lpn/PersonForm.handleChange (customer → filter companies, pick first)
function onCustomerChange(self, value) {
  const customer = self.props.customers.find((c) => c.id === parseInt(value, 10));
  const companies = self.props.companies.filter((c) => c.GateCustomer && c.GateCustomer.id === parseInt(value, 10));
  self.setDraft({
    customerId: value,
    subscriberId: customer && customer.GateSubscriber ? customer.GateSubscriber.id : 0,
    companies,
    companyId: companies && companies.length > 0 ? companies[0].id : 0,
  });
}
// verbatim build of {companies, companyId, subscriberId, customerId} for a fresh draft
function initCustomerCompany(props) {
  const customer = props.customers[0];
  if (customer && customer.id) {
    const companies = props.companies.filter((c) => c.GateCustomer && c.GateCustomer.id === customer.id);
    const company = companies && companies.length > 0 ? companies[0] : {};
    return { customerId: customer.id, subscriberId: customer.GateSubscriber.id, companies, companyId: company.id || 0 };
  }
  return { customerId: 0, subscriberId: 0, companies: [], companyId: 0 };
}

/* ============================ COMPANY config ============================ */
const COMPANY_CFG = {
  key: 'company', title: 'Companies', addLabel: 'Company', entityLabel: 'Company', entityLower: 'company', entityPlural: 'companies', icon: 'company',
  searchPlaceholder: 'Search by company name…', hasCompanyFilter: false,
  columns: { primary: (r) => r.sName, secondary: (r) => r.GateCustomer && r.GateCustomer.sName },
  saveUrl: 'api/gatecompanyNew/',
  deleteUrl: (id) => 'api/gatecompanydestroyNew/' + id,
  duplicateMessage: (d) => `Company "${d.name}" already exists for the selected customer.`,
  deleteName: (row) => row.sName,
  editorTitle: (d) => d.name || 'Company',
  validate: (d) => !!(d.name && d.name.trim()) && !!d.customerId,
  buildNewDraft: (props) => {
    const customer = props.customers[0] || {};
    return { companyId: 0, name: '', customerId: customer.id || 0, subscriberId: customer.GateSubscriber ? customer.GateSubscriber.id : 0 };
  },
  buildEditDraft: (row) => ({
    companyId: row.id, name: row.sName,
    customerId: row.GateCustomer ? row.GateCustomer.id : 0,
    subscriberId: row.GateSubscriber.id,
  }),
  buildBody: (d) => ({ id: d.companyId, sName: (d.name || '').trim(), GateCustomerId: d.customerId, GateSubscriberId: d.subscriberId }),
  renderFields: (self, draft, isNew) => (
    <div className="formgrid">
      <Field label="Customer" required>
        <SelectInput value={draft.customerId} disabled={!isNew} onChange={(e) => onCompanyCustomerChange(self, e.target.value)} placeholder="Please select">
          {self.props.customers.map((c) => <option key={c.id} value={c.id}>{c.sName}</option>)}
        </SelectInput>
      </Field>
      <Field label="Company name" required>
        <TextInput value={draft.name} placeholder="enter name" onChange={(e) => self.setDraft({ name: e.target.value })} />
      </Field>
    </div>
  ),
};
// verbatim intent of CompanyForm.handleChange (customer → subscriber), with the
// original's bug fixed to use .find (the original threw; behavior preserved is
// "subscriber follows selected customer").
function onCompanyCustomerChange(self, value) {
  const customer = self.props.customers.find((c) => parseInt(c.id, 10) === parseInt(value, 10));
  self.setDraft({ customerId: value, subscriberId: customer && customer.GateSubscriber ? customer.GateSubscriber.id : 0 });
}

/* ============================ LPN config ============================ */
const LPN_CFG = {
  key: 'lpn', title: 'License Plates', addLabel: 'Plate', entityLabel: 'LPN', entityLower: 'plate', entityPlural: 'plates', icon: 'plate',
  searchPlaceholder: 'Search by plate…', hasCompanyFilter: true,
  columns: { primary: (r) => r.sName, secondary: (r) => [r.GateCustomer && r.GateCustomer.sName, r.GateCompany && r.GateCompany.sName].filter(Boolean).join(' · ') },
  saveUrl: 'api/gatelpnNew/',
  deleteUrl: (id) => 'api/gatelpndestroyNew/' + id.toString(),
  duplicateMessage: (d) => `LPN "${d.lpn}" already exists for the selected company.`,
  deleteName: (row) => row.sName,
  editorTitle: (d) => d.lpn || 'License Plate',
  preSaveGuard: (d) => !!d.companyId,
  preSaveGuardMessage: 'Error: Company must be selected. If no company is available, please create one first.',
  validate: (d) => !!(d.lpn && d.lpn.trim()) && !!d.customerId && !!d.companyId,
  buildNewDraft: (props) => ({ lpnId: 0, lpn: '', ...initCustomerCompany(props) }),
  buildEditDraft: (row) => ({
    lpnId: row.id, lpn: row.sName,
    customerId: row.GateCustomer ? row.GateCustomer.id : 0,
    companyId: row.GateCompany ? row.GateCompany.id : 0,
    subscriberId: row.GateSubscriber.id,
    companies: null, // filled from props.companies at render via _companies
  }),
  buildBody: (d) => ({ id: d.lpnId, sName: (d.lpn || '').trim(), GateCompanyId: d.companyId, GateCustomerId: d.customerId, GateSubscriberId: d.subscriberId }),
  renderFields: (self, draft, isNew) => customerCompanyFields(self, draft, isNew, {
    extra: (s, d) => (
      <Field label="License plate number" full required>
        <TextInput value={d.lpn} placeholder="enter lpn" onChange={(e) => s.setDraft({ lpn: e.target.value })} />
      </Field>
    ),
  }),
};

/* ============================ PERSON config ============================ */
const PERSON_CFG = {
  // People are a shared roster: a person can be the DRIVER of a crossing or, where a gate has
  // "use passenger names" enabled, a named PASSENGER (who may never drive). So the catalogue is
  // "People", not "Drivers" — the driver/passenger role is decided per crossing at intake.
  key: 'person', title: 'People', addLabel: 'Person', entityLabel: 'Person', entityLower: 'person', entityPlural: 'people', icon: 'user',
  searchPlaceholder: 'Search by name…', hasCompanyFilter: true,
  columns: { primary: (r) => r.sName, secondary: (r) => [r.GateCustomer && r.GateCustomer.sName, r.GateCompany && r.GateCompany.sName].filter(Boolean).join(' · ') },
  saveUrl: 'api/gatepersonNew/',
  deleteUrl: (id) => 'api/gatepersondestroyNew/' + id,
  duplicateMessage: (d) => `A person with name "${d.firstName} ${d.lastName}" already exists for the selected company.`,
  deleteName: (row) => row.sFullName,
  editorTitle: (d) => [d.firstName, d.lastName].filter(Boolean).join(' ') || 'Person',
  preSaveGuard: (d) => !!d.companyId,
  preSaveGuardMessage: 'Error: Company must be selected. If no company is available, please create one first.',
  validate: (d) => !!(d.firstName && d.firstName.trim()) && !!(d.lastName && d.lastName.trim()) && !!d.customerId && !!d.companyId,
  buildNewDraft: (props) => ({ personId: 0, firstName: '', lastName: '', ...initCustomerCompany(props) }),
  buildEditDraft: (row) => ({
    personId: row.id, firstName: row.sFirstName, lastName: row.sLastName,
    customerId: row.GateCustomer ? row.GateCustomer.id : 0,
    companyId: row.GateCompany ? row.GateCompany.id : 0,
    subscriberId: row.GateSubscriber.id,
    companies: null,
  }),
  buildBody: (d) => ({ id: d.personId, sFirstName: (d.firstName || '').trim(), sLastName: (d.lastName || '').trim(), GateCompanyId: d.companyId, GateCustomerId: d.customerId, GateSubscriberId: d.subscriberId }),
  renderFields: (self, draft, isNew) => customerCompanyFields(self, draft, isNew, {
    extra: (s, d) => (
      <>
        <Field label="First name" required>
          <TextInput value={d.firstName} placeholder="enter first name" onChange={(e) => s.setDraft({ firstName: e.target.value })} />
        </Field>
        <Field label="Last name" required>
          <TextInput value={d.lastName} placeholder="enter last name" onChange={(e) => s.setDraft({ lastName: e.target.value })} />
        </Field>
      </>
    ),
  }),
};

/* ---------- connect() factory: one wrapper per config ---------- */
function connectCatalogue(cfg, fetchThunk, needsCompanies) {
  class Wrapper extends React.Component {
    componentDidMount() {
      // Lpn/Person forms fetch companies on mount so the Company select (editor)
      // and the company filter (list toolbar) are populated with the full list.
      if (needsCompanies) this.props.getCompanies();
    }
    render() {
      // [86bbumedw] P2 — Guard (3) / Viewer (4) get these Data screens READ-ONLY (browse + search,
      // no add/edit/delete). Admins (Super 100 / Account Admin 1 / Site Admin 2) keep full CRUD.
      const readOnly = !(this.props.authId === 100 || this.props.authId < 3);
      return (
        <CataloguePane
          cfg={{ ...cfg }}
          readOnly={readOnly}
          customers={this.props.customers}
          companies={this.props.companies}
          adminListReload={this.props.adminListReload}
          webToken={this.props.webToken}
          toast={this.props.toast}
          fetchPage={(filters) => this.props.fetchPage({ filters })}
          setAdminState={this.props.setAdminState}
          setAdminControl={this.props.setAdminControl}
        />
      );
    }
  }
  const mapState = (state) => ({
    webToken: state.auth.webToken,
    authId: state.auth.authId,
    subscriberId: state.auth.subscriberId,
    customerId: state.auth.customerId,
    adminListReload: state.admin.adminListReload,
    customers: state.customer.customers,
    companies: state.company.companies,
  });
  const mapDispatch = { setAdminState, setAdminControl, getCompanies, fetchPage: fetchThunk };
  return connect(mapState, mapDispatch)(Wrapper);
}

/*
 * NOTE ON COMPANY-BACKED EDIT DRAFTS (Lpn/Person):
 * The original forms hold `companies` filtered by the row's customer. Because
 * CataloguePane owns the draft, we hydrate the filtered `companies` inside the
 * config's renderFields via the customer→companies helper on customer change,
 * and seed it on edit from the fetched company list. To keep buildEditDraft
 * pure, the filtered list is computed lazily in customerCompanyFields against
 * self.props.companies when `draft.companies` is null.
 */
export const CompaniesPane = connectCatalogue(COMPANY_CFG, getCompanies, false);
export const LpnsPane = connectCatalogue(LPN_CFG, getLpns, true);
export const PeoplePane = connectCatalogue(PERSON_CFG, getPeople, true);
